import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { AdobeDataPages } from '@core/models/adobe-data-layer.model';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { filter } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AdobeDataLayerService } from './core/services/adobe-data-layer-service.service';
import { ConfigurationService } from './core/services/configuration.service';
import { ConnectionService } from './features/pages/select-seat/services/connection.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, AfterViewChecked {
  private cdRef = inject(ChangeDetectorRef);
  private configurationService = inject(ConfigurationService);
  private readonly loadingBarService = inject(LoadingBarService);
  router = inject(Router);
  title = 'chicago-cubs-online-group-sales';
  adobeDataLayerService = inject(AdobeDataLayerService);
  connectionService = inject(ConnectionService);

  protected readonly isHttpLoading = toSignal(
    this.loadingBarService.value$.pipe(map(value => value > 0)),
    { initialValue: false }
  );

  ngOnInit() {
    this.loadingBarService.useRef();

    if (environment.analytics && environment.analytics.mmc) {
      this.loadGtagScripts(environment.analytics.mmc);
    }
    if (environment.analytics && environment.analytics.club.length > 0) {
      environment.analytics.club.forEach(tag => {
        this.loadGTMScripts(tag);
      });
    }
    if (environment.adobeLaunchScriptSource) {
      this.loadAdobeLaunchScript(environment.adobeLaunchScriptSource);
    }

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(async event => {
        let page = event.url.split('/')[1];
        page = page.split('?')[0];
        page = page.replace(/-/g, ' ');
        page = page.charAt(0).toUpperCase() + page.slice(1);
        const gamePk = await this.adobeDataLayerService.getGamePK();
        this.adobeDataLayerService.push({
          event: 'page-view',
          page: {
            channel: 'group ticket sales',
            type: page,
          },
          ticketing: {
            events: [
              {
                event_id: this.adobeDataLayerService.getEventId() || '',
                game_pk: gamePk,
                matchup_full: this.adobeDataLayerService.getMatchupFull(),
              },
            ],
          },
          baseball: {
            team_id: this.configurationService.configuration.teamId,
          },
        });
        this.adobeDataLayerService.pageEventSubject.next(
          page as AdobeDataPages
        );
      });

    if (this.configurationService.configuration.metaPixelAnalytics) {
      this.loadMetaPixels();
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          (window as any)?.fbq?.('track', 'PageView');
        });
    }
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  loadAdobeLaunchScript(script: string) {
    const node = document.createElement('script');
    node.src = script;
    node.async = true;
    node.type = 'text/javascript';
    document.getElementsByTagName('head')[0].appendChild(node);
  }

  loadGtagScripts(analyticsId: string) {
    // first part analytics node
    const node = document.createElement('script'); // creates the script tag
    node.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`; // sets the source (insert url in between quotes)
    node.async = true; // makes script run asynchronously
    node.type = 'text/javascript'; // set the script type
    document.getElementsByTagName('head')[0].appendChild(node);
    // second part analytics node
    const node2 = document.createElement('script'); // creates the script tag
    node2.innerHTML = `window.dataLayer = window.dataLayer || [];
                      function gtag(){dataLayer.push(arguments);}
                      gtag('js', new Date());
                      gtag('config', '${analyticsId}');`;
    node2.type = 'text/javascript'; // set the script type
    // append to head of document
    document.getElementsByTagName('head')[0].appendChild(node2);
  }

  loadGTMScripts(analyticsId: string) {
    const nodeHeadGTM = document.createElement('script'); // creates the script tag
    nodeHeadGTM.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer', '${analyticsId}');
    `;
    document.getElementsByTagName('head')[0].appendChild(nodeHeadGTM);
    const nodeBodyGTM = document.createElement('noscript'); // creates the script tag
    nodeBodyGTM.innerHTML = `
    <iframe src=https://www.googletagmanager.com/ns.html?id=${analyticsId}
    height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.getElementsByTagName('body')[0].appendChild(nodeBodyGTM);
  }

  loadMetaPixels() {
    // first part analytics node
    const node = document.createElement('script'); // creates the script tag
    const noScript = document.createElement('noscript'); // creates the script tag
    node.async = true; // makes script run asynchronously
    node.type = 'text/javascript'; // set the script type
    document.getElementsByTagName('head')[0].appendChild(node);
    // second part analytics node
    node.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
                        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
                        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
                        document,'script','https://connect.facebook.net/en_US/fbevents.js');
                        fbq('init', '1410270152602121');`;
    // append to head of document
    document.getElementsByTagName('head')[0].appendChild(node);
    noScript.innerHTML = `<img height="1" width="1" style="display:none"
                        src="https://www.facebook.com/tr?id=2892474421069407&ev=PageView&noscript=1"
                        />`;
    document.getElementsByTagName('body')[0].appendChild(noScript);
  }
}
